import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { WorkflowCanvas } from '../components/workflow/WorkflowCanvas';
import { supabase } from '../lib/supabase';
import { Node, Edge } from 'reactflow';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

  type StepType =
    | 'email'
    | 'sms'
    | 'ai_call'
    | 'delay'
    | 'condition'
    | 'direct_mail'
    | 'segment_change'
    | 'wordpress_post'
    | 'webhook'
    | string;

interface DBWorkflow {
  id: string;
  name: string;
  description: string | null;
  trigger_type: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface DBWorkflowStep {
  id: string;
  workflow_id: string;
  step_order: number;
  step_type: StepType;
  name: string;
  config: Record<string, any>;
  template_id?: string | null;
  delay_amount?: number | null;
  is_active: boolean;
}

export default function WorkflowBuilderPage() {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { marketingUser } = useAuth();

  // Primary path is /admin/workflows/:id. Keep ?id= as a backwards-compatible fallback.
  const workflowId = id || searchParams.get('id');

  const [workflow, setWorkflow] = useState<DBWorkflow | null>(null);
  const [steps, setSteps] = useState<DBWorkflowStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDBBacked = !!workflowId; // If we have an ID, we treat it as DB-backed marketing_workflow (editable)

  useEffect(() => {
    if (workflowId) {
      loadDBWorkflow(workflowId);
    } else {
      setLoading(false);
    }
  }, [workflowId]);

  async function loadDBWorkflow(id: string) {
    setLoading(true);
    setError(null);
    try {
      const { data: wf, error: wfErr } = await supabase
        .from('marketing_workflows')
        .select('*')
        .eq('id', id)
        .single();

      if (wfErr) throw wfErr;

      const { data: wfSteps, error: stepsErr } = await supabase
        .from('marketing_workflow_steps')
        .select('*')
        .eq('workflow_id', id)
        .eq('is_active', true)
        .order('step_order', { ascending: true });

      if (stepsErr) throw stepsErr;

      setWorkflow(wf as DBWorkflow);
      setSteps((wfSteps || []) as DBWorkflowStep[]);
    } catch (err) {
      console.error('Error loading DB workflow:', err);
      setError('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  }

  // Build nodes/edges from DB steps for visualization
  const { nodesFromDB, edgesFromDB } = useMemo(() => {
    const nodeWidth = 260;
    const startX = 100;
    const startY = 120;
    const verticalGap = 120;

    const toNodeType = (t: StepType): string => {
      switch (t) {
        case 'email':
        case 'sms':
        case 'ai_call':
        case 'delay':
        case 'condition':
        case 'direct_mail':
          return t;
        default:
          // Map other types to a generic node we support visually
          return 'webhook';
      }
    };

    const nodes: Node[] = steps.map((s, idx) => {
      // Normalize config: merge template_id from step root into config for UI components
      const stepConfig = s.config || {};
      const normalizedConfig = {
        ...stepConfig,
        // Ensure template_id is set for email/sms step configs
        template_id: stepConfig.template_id || stepConfig.email_template_id || stepConfig.sms_template_id || s.template_id,
        delay_minutes: stepConfig.delay_minutes ?? s.delay_amount ?? 0,
      };

      return {
        id: s.id,
        type: toNodeType(s.step_type),
        position: { x: startX, y: startY + idx * verticalGap },
        data: {
          label: s.name || `${s.step_type} (Step ${s.step_order})`,
          type: s.step_type,
          config: normalizedConfig,
        },
        style: { width: nodeWidth },
      };
    });

    const edges: Edge[] = [];
    for (let i = 0; i < steps.length - 1; i++) {
      edges.push({
        id: `${steps[i].id}->${steps[i + 1].id}`,
        source: steps[i].id,
        target: steps[i + 1].id,
        type: 'smoothstep',
      });
    }

    // Prepend a trigger node (even if the workflow currently has 0 steps)
    const triggerNode: Node = {
      id: 'trigger',
      type: 'trigger',
      position: { x: startX, y: startY - verticalGap },
      data: { label: 'Membership Expired', type: 'membership_expired' },
      style: { width: nodeWidth },
    };

    const triggerEdge: Edge | null = nodes.length
      ? { id: `trigger->${nodes[0].id}`, source: 'trigger', target: nodes[0].id, type: 'smoothstep' }
      : null;

    return {
      nodesFromDB: triggerEdge ? [triggerNode, ...nodes] : [triggerNode],
      edgesFromDB: triggerEdge ? [triggerEdge, ...edges] : edges,
    };
  }, [steps]);

  const toggleActive = async () => {
    if (!workflowId || !workflow) return;

    try {
      const newActiveState = !workflow.is_active;

      const { error: updErr } = await supabase
        .from('marketing_workflows')
        .update({ is_active: newActiveState })
        .eq('id', workflowId);

      if (updErr) throw updErr;

      setWorkflow((prev) => (prev ? { ...prev, is_active: newActiveState } : null));
      alert(`Workflow ${newActiveState ? 'activated' : 'paused'} successfully!`);
    } catch (err) {
      console.error('Error toggling workflow status:', err);
      alert('Failed to toggle workflow status');
    }
  };

  // Save handler - creates new workflow or updates existing DB-backed workflow
  const handleDesignerSave = async (nodes: Node[], edges: Edge[]) => {
    try {
      setSaving(true);
      setError(null);

      if (isDBBacked) {
        // Update existing workflow steps
        await saveWorkflowSteps(nodes, edges);
      } else {
        // Create new workflow
        const workflowName = prompt('Enter workflow name:');
        if (!workflowName) return;

        const workflowDescription = prompt('Enter workflow description (optional):') || '';
        const triggerType = prompt('Enter trigger type (e.g., membership_expired, inactivity):') || 'manual';

        const { data, error } = await supabase
          .from('marketing_workflows')
          .insert({
            name: workflowName,
            description: workflowDescription,
            trigger_type: triggerType,
            company_id: marketingUser?.company_ids?.[0] || 'halesac',
            is_active: false,
          })
          .select()
          .single();

        if (error) throw error;

        // Save initial steps
        await saveWorkflowSteps(nodes, edges, data.id);

        // Navigate to the DB-backed view
        navigate(`/admin/workflows/${data.id}`);
        alert('Workflow created successfully!');
      }
    } catch (err) {
      console.error('Error saving workflow:', err);
      setError('Failed to save workflow');
      alert('Failed to save workflow. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  const saveWorkflowSteps = async (nodes: Node[], edges: Edge[], wfId?: string) => {
    const targetWorkflowId = wfId || workflowId;
    if (!targetWorkflowId) return;

    try {
      // Filter out trigger node
      const stepNodes = nodes
        .filter((n) => n.type !== 'trigger')
        // Establish a deterministic order from the canvas layout
        .sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0));

      // Delete existing steps
      await supabase
        .from('marketing_workflow_steps')
        .delete()
        .eq('workflow_id', targetWorkflowId);

      // Insert new steps
      const stepsToInsert = stepNodes.map((node, index) => {
        const baseConfig = (node.data?.config || {}) as Record<string, any>;
        const config: Record<string, any> = {
          ...baseConfig,
          type: node.data?.type || node.type,
        };

        // Normalize common keys (snake_case UI vs camelCase engine)
        const templateId = config.template_id ?? config.templateId ?? null;
        if (templateId) {
          config.template_id = templateId;
          config.templateId = templateId;
        }

        // Add delay amount if it's a delay step
        let delayAmount: number | null = null;
        if (node.type === 'delay') {
          let minutes = config.delay_minutes ?? config.delayMinutes;

          if (typeof minutes !== 'number' || minutes <= 0) {
            // Fallback: infer from label
            const label = String(node.data?.label || '');
            if (label.toLowerCase().includes('day')) {
              const days = parseInt(label.match(/\d+/)?.[0] || '1');
              minutes = days * 1440;
            } else if (label.toLowerCase().includes('hour')) {
              const hours = parseInt(label.match(/\d+/)?.[0] || '1');
              minutes = hours * 60;
            } else {
              minutes = 1440;
            }
          }

          delayAmount = minutes;
          config.delay_minutes = minutes;
          config.delayMinutes = minutes;
        }

        return {
          workflow_id: targetWorkflowId,
          step_order: index + 1,
          step_type: (node.data?.type || node.type || 'email') as string,
          name: node.data.label || `Step ${index + 1}`,
          config: config,
          template_id: templateId,
          delay_amount: delayAmount,
          is_active: true,
        };
      });

      if (stepsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('marketing_workflow_steps')
          .insert(stepsToInsert);

        if (insertError) throw insertError;
      }

      alert('Workflow steps saved successfully!');
      
      // Reload the workflow if we're on an existing one
      if (workflowId) {
        await loadDBWorkflow(workflowId);
      }
    } catch (err) {
      console.error('Error saving workflow steps:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading workflow...</p>
        </div>
      </div>
    );
  }

  const headerTitle = workflow ? workflow.name : 'New Workflow';
  const headerDesc = workflow?.description || '';

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/workflows')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Workflows
          </button>

          <div className="border-l border-gray-300 pl-4">
            <h1 className="text-xl font-semibold text-gray-900">
              {headerTitle}
            </h1>
            {headerDesc && (
              <p className="text-sm text-gray-500">{headerDesc}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {workflow && (
            <button
              onClick={toggleActive}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                workflow.is_active
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {workflow.is_active ? (
                <>
                  <Pause className="w-4 h-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Activate
                </>
              )}
            </button>
          )}

          {saving && (
            <span className="text-sm text-gray-500">Saving...</span>
          )}

          {workflow?.is_active && (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              Active
            </span>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 min-h-[600px]">
        <WorkflowCanvas
          workflowId={workflowId || undefined}
          initialNodes={isDBBacked ? nodesFromDB : undefined}
          initialEdges={isDBBacked ? edgesFromDB : undefined}
          onSave={handleDesignerSave}
          readOnly={false}
        />
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border-t border-blue-200 px-6 py-3">
        <p className="text-sm text-blue-800">
          💡 Tip: Click "Add Step" to add workflow actions, drag to reorder, and click "Save" to persist changes.
          {isDBBacked && ' Changes are saved directly to the database.'}
        </p>
      </div>
    </div>
  );
}
